import styles from "./Layout.module.css";
import {
  ShieldLockRegular,
  ShieldKeyholeRegular,
  ShieldDismissFilled,
  HeartPulseErrorRegular
} from "@fluentui/react-icons";
import { ReactNode, useEffect } from "react";
import { 
  OpenAiModel,
  SearchIndex,
} from "../../api";
import { 
  Stack,
} from "@fluentui/react";
import { useShell } from "../../shell/context/ShellContext";

export type LayoutProps = {
  children: ReactNode;
  onAvailableModelsChanged?: (models: OpenAiModel[]) => void;
  onAvailableIndexesChanged?: (indexes: SearchIndex[]) => void;
};

const Layout = ({ children, onAvailableModelsChanged, onAvailableIndexesChanged }: LayoutProps) => {

  const { authGateStatus, userInfo, availableModels, availableIndexes } = useShell();

  // Forward models/indexes to parent callbacks when they arrive.
  // These are already fetched by ShellContext; we just notify the consuming component.
  useEffect(() => {
    if (authGateStatus === "ok" && onAvailableModelsChanged && availableModels.length > 0) {
      onAvailableModelsChanged(availableModels);
    }
  }, [authGateStatus, availableModels, onAvailableModelsChanged]);

  useEffect(() => {
    if (authGateStatus === "ok" && onAvailableIndexesChanged && availableIndexes.length > 0) {
      onAvailableIndexesChanged(availableIndexes);
    }
  }, [authGateStatus, availableIndexes, onAvailableIndexesChanged]);

  // Helper function to get the appropriate sign-in URL.
  // The post_login_redirect_uri param tells Azure Static Web Apps (and the local
  // SWA emulator) to send the user back to the page they were trying to access
  // (e.g. /share/<conversationId>) instead of the app root after a successful login.
  const getSignInUrl = () => {
    const redirectPath = window.location.pathname + window.location.search + window.location.hash;
    const redirectParam = `?post_login_redirect_uri=${encodeURIComponent(redirectPath)}`;
    const isLocal = window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";
    if (isLocal) {
      return `http://localhost:4280/.auth/login/local${redirectParam}`;
    }
    return `/login${redirectParam}`;
  };

  // Auto-redirect to login when the user has no Azure session at all, preserving
  // the original URL via post_login_redirect_uri so they land back on the right
  // page (e.g. a shared conversation link) after signing in.
  // Only redirect when clientPrincipal is absent — i.e. the user is truly not
  // logged in. If they ARE Azure-authenticated but the backend says unauthorized,
  // redirecting would cause an infinite loop; we show an error screen instead.
  useEffect(() => {
    if (authGateStatus === "unauthorized" && !userInfo?.clientPrincipal) {
      window.location.replace(getSignInUrl());
    }
  }, [authGateStatus, userInfo]); // eslint-disable-line react-hooks/exhaustive-deps

  // While ShellContext is still loading auth, render nothing (avoids flash of error state)
  if (authGateStatus === "loading") {
    return <div className={styles.layout} />;
  }

  // Redirect is in flight (useEffect above) — render blank to avoid a flash of the error UI.
  if (authGateStatus === "unauthorized" && !userInfo?.clientPrincipal) {
    return <div className={styles.layout} />;
  }

  return (
    <div className={styles.layout}>

      {/*Configure Authentication*/}
      {authGateStatus === "configure-auth" ? (
        <Stack className={styles.chatEmptyState}>
          <ShieldLockRegular
            className={styles.chatIcon}
            style={{ color: "darkorange", height: "200px", width: "200px" }}
          />
          <h1 className={styles.chatEmptyStateTitle}>
            Authentication Not Configured
          </h1>
          <h2 className={styles.chatEmptyStateSubtitle}>
            This app does not have authentication configured. Please add an
            identity provider by finding your app in the{" "}
            <a href="https://portal.azure.com/" target="_blank">
              Azure Portal
            </a>{" "}
            and following{" "}
            <a
              href="https://learn.microsoft.com/en-us/azure/app-service/scenario-secure-app-authentication-app-service#3-configure-authentication-and-authorization"
              target="_blank"
            >
              these instructions
            </a>
            .
          </h2>
          <h2
            className={styles.chatEmptyStateSubtitle}
            style={{ fontSize: "20px" }}
          >
            <strong>
              Authentication configuration takes a few minutes to apply.{" "}
            </strong>
          </h2>
          <h2
            className={styles.chatEmptyStateSubtitle}
            style={{ fontSize: "20px" }}
          >
            <strong>
              If you deployed in the last 10 minutes, please wait and reload the
              page after 10 minutes.
            </strong>
          </h2>
        </Stack>
      ) : (null)}      

      {/*Unauthorized - Azure session exists but backend does not recognise this user.
          Redirecting to login would loop, so show an error instead.*/}
      {authGateStatus === "unauthorized" ? (
        <Stack className={styles.chatEmptyState}>
          <ShieldKeyholeRegular
            className={styles.chatIcon}
            style={{ color: "red", height: "200px", width: "200px" }}
          />
          <h1 className={styles.chatEmptyStateTitle}>
            Unauthorized
          </h1>
          <h2 className={styles.chatEmptyStateSubtitle}>
            Your account does not have access to this application. Please contact your administrator.
          </h2>
        </Stack>
      ) : (null)}

      {/*Access Denied*/}
      {authGateStatus === "access-denied" ? (
        <Stack className={styles.chatEmptyState}>
          <ShieldDismissFilled
            className={styles.chatIcon}
            style={{ color: "red", height: "200px", width: "200px" }}
          />
          <h1 className={styles.chatEmptyStateTitle}>
            Access Denied
          </h1>
          <h2 className={styles.chatEmptyStateSubtitle}>
            You don't have the required permissions to access this app.
          </h2>          
        </Stack>
      ) : (null)}      
      
      {/*Backend connection failed*/}
      {authGateStatus === "backend-failed" ? (
        <Stack className={styles.chatEmptyState}>
          <HeartPulseErrorRegular
            className={styles.chatIcon}
            style={{ color: "red", height: "200px", width: "200px" }}
          />
          <h1 className={styles.chatEmptyStateTitle}>
            App offline
          </h1>
          <h2 className={styles.chatEmptyStateSubtitle}>
            Refresh the page, try again later or contact your administrator if this problem persists.
          </h2>          
        </Stack>
      ) : (null)}   

      {authGateStatus === "ok" ? (<>{children}</>) : null}
              
    </div>
  );
};

export default Layout;
